/**
 * Universal tabs — the workbench's one tab model.
 *
 * Historically "tabs" meant editor file tabs only (`openTabs: string[]`) while
 * a separate `mode` slice decided what the centre pane showed. This module
 * unifies both: a `Tab` is ANY view — the board, an open `.arch` file, a
 * rendered doc, a process, the types view — and an ordered `Tab[]` + an
 * `activeTabId` is the single source of truth for "what is open / showing".
 *
 * The store keeps the legacy slices (`mode`, `openTabs`, `activeFile`) alive as
 * DERIVED mirrors of this model so existing primitives + tests need no churn:
 *   - `mode`       = the active tab's kind, mapped (file|board → "board").
 *   - `openTabs`   = the paths of the file-kind tabs, in order.
 *   - `activeFile` = the active file tab's path, else the last file tab touched.
 *
 * Tab identity is its `id`. Ids are deterministic per identity so "open X again"
 * activates the existing tab instead of duplicating it, and so a file tab's id
 * IS its path (the editor-tab call sites pass paths and keep working). Every
 * NON-file id carries the {@link NON_FILE_TAB_NS} NUL prefix so it can never
 * collide with a file path:
 *   - file:    the path                          (one tab per open file)
 *   - board:   `␀board:${rootId ?? "__root__"}`  (one overview + one per entity)
 *   - doc:     `␀doc`                            (singleton, re-pointed)
 *   - process: `␀process:${processId}`
 *   - types:   `␀types`                          (singleton)
 *
 * Per-tab state (board camera/root, the doc anchor, …) lives INSIDE the tab, so
 * persistence is just serialising the array.
 */
import { isMarkdownPath, type Vfs } from "@archlang/viewer-core";
import type { BoardViewport, WorkspaceMode } from "./WorkspaceStore.js";
import type { PanelKind, JsonValue, RemovedEntityRef } from "./plugins/types.js";

/** Resolve a custom tab kind to its registered {@link PanelKind}, or undefined
 *  when no plugin owns it. The store is handed one so its (otherwise pure) tab
 *  switches can delegate the `custom` arm; an unresolved kind → the tab drops on
 *  reload (an unknown-panelKind is treated like an apiVersion mismatch). */
export type PanelKindResolver = (kind: string) => PanelKind | undefined;

export type TabKind = "board" | "file" | "doc" | "process" | "types" | "view" | "module" | "custom";

/** A board view, optionally rooted/focused on one entity (cmd-click). */
export interface BoardTabState {
  /** Stable id of the entity this board is rooted on; null = whole workspace. */
  readonly rootId: string | null;
  /** VFS path whose package this board renders (drives the board `rootPath`). */
  readonly rootPath: string | null;
  /** `rootId` names a space (has nested contents) → the board roots INTO it
   *  (focal = the space) instead of just framing the node. */
  readonly rootIsSpace?: boolean;
  /** This tab's own camera (independent of every other board tab). */
  readonly viewport: BoardViewport | null;
}
export interface FileTabState {
  readonly path: string;
  /** Camera of the companion board shown beside the editor in this file tab. */
  readonly viewport: BoardViewport | null;
  /** Preview (transient) tab — opened by a single click in the nav. A new
   *  preview reuses this one slot; a double-click pins it (preview = false).
   *  Mirrors VS Code's italic preview tab. */
  readonly preview?: boolean;
}
/** A markdown document open in the CM6 document surface. Singleton (one doc tab
 *  re-pointed on each open) so switching docs is fast and doesn't pile up tabs.
 *  Keyed by VFS path; `slug` is the section anchor to scroll to (deep-link). */
export interface DocTabState {
  readonly path: string;
  readonly slug?: string;
}
export interface ProcessTabState {
  readonly processId: string;
}
export interface TypesTabState {
  readonly typeName: string | null;
}
/** A dedicated PAGE for one module/service — its rich detail (interfaces,
 *  connections, processes, aspects, children), not a board. Keyed by the module's
 *  qualified key (the node-universe id). `moduleName` labels the tab without a
 *  model lookup. */
export interface ModuleTabState {
  readonly moduleId: string;
  readonly moduleName?: string;
}
/** A v0.10 view open on its own tab — renders the board surface region with this
 *  view active (the representation switch picks board/table/matrix/flow). */
export interface ViewTabState {
  readonly viewName: string;
  /** This tab's own board camera (for board-representation views). */
  readonly viewport: BoardViewport | null;
}
/** A plugin-owned custom tab (the `panelKinds` seam). The store owns tab
 *  identity + lifecycle; the plugin owns only its opaque `state` and how to
 *  (de)serialize it. `panelKind` keys the {@link PanelKind} registry. */
export interface CustomTabState {
  readonly pluginId: string;
  readonly panelKind: string;
  readonly state: JsonValue;
}

export type Tab =
  | { readonly id: string; readonly kind: "board"; readonly state: BoardTabState }
  | { readonly id: string; readonly kind: "file"; readonly state: FileTabState }
  | { readonly id: string; readonly kind: "doc"; readonly state: DocTabState }
  | { readonly id: string; readonly kind: "process"; readonly state: ProcessTabState }
  | { readonly id: string; readonly kind: "types"; readonly state: TypesTabState }
  | { readonly id: string; readonly kind: "view"; readonly state: ViewTabState }
  | { readonly id: string; readonly kind: "module"; readonly state: ModuleTabState }
  | { readonly id: string; readonly kind: "custom"; readonly state: CustomTabState };

/** Sentinel rootId for the whole-workspace overview board. */
export const ROOT_BOARD_ID = "__root__";

/** Namespace prefix for every NON-file tab id. NUL cannot appear in a file
 *  path, so no non-file tab id can ever equal (or collide with) a file tab's
 *  id — a file tab's id IS its path, and without the prefix a file literally
 *  named "doc" (or "types", "board:__root__", …: all legal names) shared the
 *  doc singleton's id, so openTab/createFile dedup activated the WRONG tab
 *  and renameFile could mint duplicate ids (the r16 namespace-collision
 *  class; same cure as the NUL-separated clone ids). */
export const NON_FILE_TAB_NS = "\u0000";

/** The overview board's deterministic id (the pinned home tab). */
export const OVERVIEW_TAB_ID = `${NON_FILE_TAB_NS}board:${ROOT_BOARD_ID}`;
/** The doc singleton's deterministic id. */
export const DOC_TAB_ID = `${NON_FILE_TAB_NS}doc`;
/** The types singleton's deterministic id. */
export const TYPES_TAB_ID = `${NON_FILE_TAB_NS}types`;

/** Migrate PERSISTED tabs into this build's invariants and report the
 *  old→new id map so callers can migrate `activeTabId` + view-record keys.
 *  Idempotent — a legal snapshot passes through untouched. Two passes:
 *
 *  1. Non-file ids minted before the NUL namespace gain the prefix.
 *  2. The doc law + id uniqueness heal: snapshots minted by older builds can
 *     carry a file-kind tab on a markdown path (the pre-r23 rename bug
 *     persisted them — "surviving reload"), a doc tab on a non-markdown
 *     path, or duplicate DOC_TAB_ID entries. A markdown file tab becomes the
 *     doc singleton (first claims it), a non-markdown doc tab becomes a file
 *     tab, and any tab whose (healed) id is already taken drops. Without
 *     this, a bad blob restores its violation verbatim on every boot,
 *     forever. */
export function namespacePersistedTabs(tabs: readonly Tab[]): {
  readonly tabs: readonly Tab[];
  readonly idMap: ReadonlyMap<string, string>;
} {
  const idMap = new Map<string, string>();
  let changed = false;
  const namespaced = tabs.map((t): Tab => {
    if (t.kind === "file" || t.id.startsWith(NON_FILE_TAB_NS)) return t;
    const id = `${NON_FILE_TAB_NS}${t.id}`;
    idMap.set(t.id, id);
    changed = true;
    return { ...t, id } as Tab;
  });
  const have = new Set<string>();
  const next: Tab[] = [];
  for (const t of namespaced) {
    let out: Tab = t;
    if (t.kind === "file" && isMarkdownPath(t.state.path)) {
      out = { id: DOC_TAB_ID, kind: "doc", state: { path: t.state.path } };
    } else if (t.kind === "doc" && !isMarkdownPath(t.state.path)) {
      out = { id: t.state.path, kind: "file", state: { path: t.state.path, viewport: null } };
    }
    if (have.has(out.id)) {
      // Dropped as a duplicate — it MERGES into the surviving tab with this
      // id (same document, one singleton). Re-point its (chained) source
      // onto the survivor so `activeTabId` and view-record keys follow the
      // document instead of dying with the dropped tab. (An earlier cleanup
      // DELETED mappings here — but a dropped-duplicate's id is by
      // definition a LIVE id, so mappings onto it point at the survivor and
      // must stay/repoint, never vanish.)
      let source = t.id;
      for (const [oldId, newId] of idMap) if (newId === t.id) source = oldId;
      if (source !== out.id) idMap.set(source, out.id);
      changed = true;
      continue;
    }
    have.add(out.id);
    if (out !== t) {
      changed = true;
      // Chain onto a pass-1 re-mint if one happened (old blob id → healed id).
      let source = t.id;
      for (const [oldId, newId] of idMap) if (newId === t.id) source = oldId;
      idMap.set(source, out.id);
    }
    next.push(out);
  }
  return changed ? { tabs: next, idMap } : { tabs, idMap };
}

/** Open-spec (no id — derived) handed to `openTab`. */
export type TabSpec =
  | { readonly kind: "board"; readonly rootId?: string | null; readonly rootPath?: string | null; readonly rootIsSpace?: boolean; readonly viewport?: BoardViewport | null }
  | { readonly kind: "file"; readonly path: string; readonly viewport?: BoardViewport | null; readonly preview?: boolean }
  | { readonly kind: "doc"; readonly path: string; readonly slug?: string }
  | { readonly kind: "process"; readonly processId: string }
  | { readonly kind: "types"; readonly typeName?: string | null }
  | { readonly kind: "view"; readonly viewName: string; readonly viewport?: BoardViewport | null }
  | { readonly kind: "module"; readonly moduleId: string; readonly moduleName?: string }
  | { readonly kind: "custom"; readonly pluginId: string; readonly panelKind: string; readonly state: JsonValue };

/** Deterministic id for a tab spec — also its dedup key. The `custom` arm
 *  delegates to the resolved {@link PanelKind}'s `idFor` (the plugin's dedup
 *  key); with no resolver / unknown kind it falls back to plugin+kind alone. */
export function tabIdForSpec(spec: TabSpec, resolve?: PanelKindResolver): string {
  switch (spec.kind) {
    case "file":
      return spec.path;
    case "board":
      return `${NON_FILE_TAB_NS}board:${spec.rootId ?? ROOT_BOARD_ID}`;
    case "doc":
      // Singleton — one doc tab, re-pointed on each open (fast doc switching).
      return DOC_TAB_ID;
    case "process":
      return `${NON_FILE_TAB_NS}process:${spec.processId}`;
    case "types":
      return TYPES_TAB_ID;
    case "view":
      return `${NON_FILE_TAB_NS}view:${spec.viewName}`;
    case "module":
      return `${NON_FILE_TAB_NS}module:${spec.moduleId}`;
    case "custom": {
      let sub = "";
      try {
        sub = resolve?.(spec.panelKind)?.idFor(spec) ?? "";
      } catch {
        sub = "";
      }
      return `${NON_FILE_TAB_NS}custom:${spec.pluginId}:${spec.panelKind}${sub ? `:${sub}` : ""}`;
    }
  }
}

/** Build a full Tab from a spec (filling per-tab state defaults). */
export function makeTab(spec: TabSpec, resolve?: PanelKindResolver): Tab {
  const id = tabIdForSpec(spec, resolve);
  switch (spec.kind) {
    case "file":
      return {
        id,
        kind: "file",
        state: { path: spec.path, viewport: spec.viewport ?? null, preview: spec.preview },
      };
    case "board":
      return {
        id,
        kind: "board",
        state: {
          rootId: spec.rootId ?? null,
          rootPath: spec.rootPath ?? null,
          rootIsSpace: spec.rootIsSpace ?? false,
          viewport: spec.viewport ?? null,
        },
      };
    case "doc":
      return { id, kind: "doc", state: { path: spec.path, slug: spec.slug } };
    case "process":
      return { id, kind: "process", state: { processId: spec.processId } };
    case "types":
      return { id, kind: "types", state: { typeName: spec.typeName ?? null } };
    case "view":
      return { id, kind: "view", state: { viewName: spec.viewName, viewport: spec.viewport ?? null } };
    case "module":
      return { id, kind: "module", state: { moduleId: spec.moduleId, moduleName: spec.moduleName } };
    case "custom":
      return {
        id,
        kind: "custom",
        state: { pluginId: spec.pluginId, panelKind: spec.panelKind, state: spec.state },
      };
  }
}

/** The legacy `mode` a given active tab maps to (the centre-pane perspective).
 *  file and board both render the board surface → "board". */
export function modeForTab(tab: Tab | undefined): WorkspaceMode {
  switch (tab?.kind) {
    case "doc":
      return "docs";
    case "process":
      return "process";
    case "types":
      return "types";
    case "file":
    case "board":
    case "view": // a view tab renders the board surface region (the switch picks board/table/matrix/flow)
    default:
      return "board";
  }
}

/** The single board/file tab id that `setMode(target)` should activate, given
 *  the current tabs + which file/board tab was most recently active. Returns
 *  null when no suitable existing tab exists (caller creates one). */
export function tabIdForMode(
  mode: WorkspaceMode,
  tabs: readonly Tab[],
  activeTabId: string | null,
): string | null {
  if (mode === "docs") return tabs.find((t) => t.kind === "doc")?.id ?? null;
  if (mode === "process") return tabs.find((t) => t.kind === "process")?.id ?? null;
  if (mode === "types") return tabs.find((t) => t.kind === "types")?.id ?? null;
  // "board": keep the active tab if it already renders the board surface,
  // else the first board tab, else the first file tab.
  const active = tabs.find((t) => t.id === activeTabId);
  if (active && (active.kind === "board" || active.kind === "file")) return active.id;
  return (
    tabs.find((t) => t.kind === "board")?.id ??
    tabs.find((t) => t.kind === "file")?.id ??
    null
  );
}

/** Paths of the file-kind tabs, in order. Returns the previous array when the
 *  content is identical so the `openTabs` slice keeps a stable identity. */
export function fileTabPaths(
  tabs: readonly Tab[],
  prev: readonly string[],
): readonly string[] {
  const next: string[] = [];
  for (const t of tabs) if (t.kind === "file") next.push(t.state.path);
  if (next.length === prev.length && next.every((p, i) => p === prev[i])) return prev;
  return next;
}

/**
 * Drop file AND doc tabs whose path no longer exists in `files` (a doc tab's
 * identity is a VFS `.md` path, same as a file tab's — only its surface
 * differs). If the active tab was a dropped tab, re-point to a neighbouring
 * tab (left, else right, else any). Board tabs whose `rootPath` file vanished
 * fall back to the overview. process/types tabs reference MODEL entities (not
 * files) and are pruned by a UI effect, not here. Returns
 * `{ tabs, activeTabId }` (same refs if stable).
 */
export function reconcileFileTabs(
  tabs: readonly Tab[],
  files: Vfs,
  activeTabId: string | null,
): { tabs: readonly Tab[]; activeTabId: string | null } {
  const activeIdx = tabs.findIndex((t) => t.id === activeTabId);
  const kept: Tab[] = [];
  for (const t of tabs) {
    if (t.kind === "file" && !files.has(t.state.path)) continue;
    if (t.kind === "doc" && !files.has(t.state.path)) continue;
    if (t.kind === "board" && t.state.rootPath !== null && !files.has(t.state.rootPath)) {
      // Keep the tab but unroot it to the overview rather than closing.
      kept.push({ ...t, state: { ...t.state, rootId: null, rootPath: null } });
      continue;
    }
    kept.push(t);
  }
  if (kept.length === tabs.length && activeIdx !== -1) {
    // Possibly only board-tab unrooting changed; detect by reference.
    const same = kept.every((t, i) => t === tabs[i]);
    if (same) return { tabs, activeTabId };
    return { tabs: kept, activeTabId };
  }
  let nextActive = activeTabId;
  if (activeTabId !== null && !kept.some((t) => t.id === activeTabId)) {
    // Pick a neighbour relative to the removed tab's old position.
    nextActive =
      kept[Math.min(activeIdx, kept.length - 1)]?.id ??
      kept[activeIdx - 1]?.id ??
      kept[0]?.id ??
      null;
  }
  return { tabs: kept, activeTabId: nextActive };
}

/** A bare path resolves to its tab spec: `.md` is a document (the singleton
 *  CM6 `doc` tab is the only markdown surface — never Monaco), everything else
 *  is a `file` tab. Mirrors the extension check `storeNavSource`'s nav-click
 *  dispatch already applies for `navDocs` membership, but works from the path
 *  alone so it also covers seeding/hydration/renames, which have no
 *  model-derived doc list. */
export function specForPath(path: string): TabSpec {
  return isMarkdownPath(path) ? { kind: "doc", path } : { kind: "file", path };
}

export interface RenameInTabsResult {
  readonly tabs: readonly Tab[];
  /** EXACT old→new id map for surviving re-keyed tabs (a dropped tab — a
   *  Monaco clone whose rename collided with the taken doc singleton — has
   *  no entry). Kind conversions are included (file id → the doc id). */
  readonly idMap: ReadonlyMap<string, string>;
}

/** Rewrite a renamed path inside file, doc, and board-rootPath tab state.
 *
 *  Extension-changing renames re-route the tab KIND through the `specForPath`
 *  law: a file tab renamed into `.md` becomes the doc singleton (or drops,
 *  when the singleton is already open — which then re-points to the renamed
 *  document, mirroring openTab's doc arm); the doc tab renamed out of `.md`
 *  converts to a regular file tab. Without this, a nav inline rename with a
 *  typed extension left markdown open in Monaco (or source in CM6). */
export function renameInTabs(
  tabs: readonly Tab[],
  from: string,
  to: string,
): RenameInTabsResult {
  const toSpec = specForPath(to);
  const idMap = new Map<string, string>();
  const hasDocTab = tabs.some((t) => t.kind === "doc");
  // A file tab renaming INTO markdown hijacks the doc singleton — an already-
  // open doc tab re-points to the renamed document even though its own path
  // doesn't match `from`.
  const fileToDoc =
    toSpec.kind === "doc" && tabs.some((t) => t.kind === "file" && t.state.path === from);
  let changed = false;
  let docClaimed = false;
  const next: Tab[] = [];
  for (const t of tabs) {
    if (t.kind === "file" && t.state.path === from) {
      changed = true;
      if (toSpec.kind === "doc") {
        if (!hasDocTab && !docClaimed) {
          docClaimed = true;
          idMap.set(t.id, DOC_TAB_ID);
          next.push({ id: DOC_TAB_ID, kind: "doc", state: { path: to } });
        }
        // else the singleton is taken (or an earlier clone claimed it) — the
        // Monaco tab drops; the singleton can't hold copies.
        continue;
      }
      // A file tab's id is its path — but a DUPLICATED tab carries a `␀N`
      // suffix while sharing the path. Preserve the suffix so a rename never
      // collapses the original and its clones onto one id.
      const suffix = t.id.startsWith(from) ? t.id.slice(from.length) : "";
      idMap.set(t.id, to + suffix);
      next.push({ ...t, id: to + suffix, kind: "file", state: { ...t.state, path: to } });
      continue;
    }
    if (t.kind === "doc" && (t.state.path === from || fileToDoc)) {
      changed = true;
      if (toSpec.kind === "file") {
        // Renamed out of markdown (t.state.path === from here): the CM6 doc
        // surface must not render a source file — convert to a file tab.
        idMap.set(t.id, to);
        next.push({ id: to, kind: "file", state: { path: to, viewport: null } });
      } else if (t.state.path === from) {
        // Plain document rename — the singleton re-points (slug survives).
        next.push({ ...t, kind: "doc", state: { ...t.state, path: to } });
      } else {
        // Hijack by a file→doc rename — show the renamed document (fresh
        // anchor, like openTab's re-point).
        next.push({ ...t, kind: "doc", state: { path: to } });
      }
      continue;
    }
    if (t.kind === "board" && t.state.rootPath === from) {
      changed = true;
      next.push({ ...t, kind: "board", state: { ...t.state, rootPath: to } });
      continue;
    }
    next.push(t);
  }
  return { tabs: changed ? next : tabs, idMap };
}

// ---- custom (plugin) tabs -------------------------------------------------

/** Re-point the active id after `kept` dropped some of `tabs`: prefer the tab
 *  now at the removed one's old index (its right neighbour), else the left, else
 *  the first. Shared by the custom-tab reconcilers (mirrors reconcileFileTabs). */
function repointActive(
  tabs: readonly Tab[],
  kept: readonly Tab[],
  activeTabId: string | null,
): string | null {
  if (activeTabId === null || kept.some((t) => t.id === activeTabId)) return activeTabId;
  const activeIdx = tabs.findIndex((t) => t.id === activeTabId);
  return (
    kept[Math.min(activeIdx, kept.length - 1)]?.id ??
    kept[activeIdx - 1]?.id ??
    kept[0]?.id ??
    null
  );
}

/**
 * Drop custom tabs invalidated by a recompile: for each removed model entity,
 * ask the tab's PanelKind `onEntityRemoved` — a `"close"` verdict drops it (a
 * missing hook, an unresolved kind, or a throw all keep). Peer of
 * `reconcileFileTabs` for the plugin-owned arm. Returns the same refs when
 * nothing changed.
 */
export function reconcileCustomTabs(
  tabs: readonly Tab[],
  activeTabId: string | null,
  removed: readonly RemovedEntityRef[],
  resolve: PanelKindResolver,
): { tabs: readonly Tab[]; activeTabId: string | null } {
  if (removed.length === 0) return { tabs, activeTabId };
  const kept = tabs.filter((t) => {
    if (t.kind !== "custom") return true;
    const pk = resolve(t.state.panelKind);
    if (!pk?.onEntityRemoved) return true;
    for (const ref of removed) {
      let verdict: "keep" | "close" = "keep";
      try {
        verdict = pk.onEntityRemoved(t.state.state, ref);
      } catch {
        verdict = "keep";
      }
      if (verdict === "close") return false;
    }
    return true;
  });
  if (kept.length === tabs.length) return { tabs, activeTabId };
  return { tabs: kept, activeTabId: repointActive(tabs, kept, activeTabId) };
}

/**
 * On reload, revive each custom tab through its PanelKind and drop the ones
 * that can't come back: an unresolved kind (no plugin registered it) or a
 * `revive` returning null/throwing — the apiVersion-mismatch posture. Survivors
 * carry their revived state. Built-in tabs pass through untouched. Returns the
 * same refs when every custom tab survived unchanged.
 */
export function reviveCustomTabs(
  tabs: readonly Tab[],
  activeTabId: string | null,
  resolve: PanelKindResolver,
): { tabs: readonly Tab[]; activeTabId: string | null } {
  let changed = false;
  const kept: Tab[] = [];
  for (const t of tabs) {
    if (t.kind !== "custom") {
      kept.push(t);
      continue;
    }
    const pk = resolve(t.state.panelKind);
    let revived: JsonValue | null = null;
    if (pk) {
      try {
        revived = pk.revive(t.state.state);
      } catch {
        revived = null;
      }
    }
    if (revived === null) {
      changed = true; // dropped
      continue;
    }
    if (revived === t.state.state) {
      kept.push(t);
    } else {
      changed = true;
      kept.push({ ...t, state: { ...t.state, state: revived } });
    }
  }
  if (!changed) return { tabs, activeTabId };
  return { tabs: kept, activeTabId: repointActive(tabs, kept, activeTabId) };
}

/** Map custom tabs through their PanelKind `serialize` for persistence; built-in
 *  tabs pass through. Returns the same ref when nothing changed. A throwing
 *  serialize keeps the live state (best-effort — reload-revive still guards). */
export function serializeCustomTabs(
  tabs: readonly Tab[],
  resolve: PanelKindResolver,
): readonly Tab[] {
  let changed = false;
  const out = tabs.map((t): Tab => {
    if (t.kind !== "custom") return t;
    const pk = resolve(t.state.panelKind);
    if (!pk) return t;
    let ser: JsonValue = t.state.state;
    try {
      ser = pk.serialize(t.state.state);
    } catch {
      ser = t.state.state;
    }
    if (ser === t.state.state) return t;
    changed = true;
    return { ...t, state: { ...t.state, state: ser } };
  });
  return changed ? out : tabs;
}

// ---- persistence ----------------------------------------------------------

/** Persisted tab snapshot (a slice of the overlay blob). */
export interface TabsSnapshot {
  readonly tabs: readonly Tab[];
  readonly activeTabId: string | null;
}

/**
 * Migrate a legacy overlay (file `openTabs` + `activeFile`, no tab model) into
 * a universal tab snapshot: one file tab per open path + a single overview
 * board tab, active = the legacy active file's tab (else the board).
 */
export function migrateOverlay(input: {
  readonly openTabs?: readonly string[];
  readonly activeFile?: string | null;
  readonly boardViewport?: BoardViewport | null;
}): TabsSnapshot {
  const fileTabs: Tab[] = (input.openTabs ?? []).map((path) =>
    makeTab({ kind: "file", path }),
  );
  const board = makeTab({ kind: "board", viewport: input.boardViewport ?? null });
  const tabs: Tab[] = [board, ...fileTabs];
  const activeTabId =
    input.activeFile && fileTabs.some((t) => t.id === input.activeFile)
      ? input.activeFile
      : board.id;
  return { tabs, activeTabId };
}
